// client/src/pages/School_Admin/Academics/ExamControlCenterPage.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Chip,
  LinearProgress,
  Tabs,
  Tab,
  Snackbar,
  Alert,
  Box
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {fetchJson} from '../../../utils/fetchJson';
import AppLoader from '../../../components/AppLoader';

const ExamControlCenterPage = () => {
  const { t } = useTranslation();
  // Context-aware cache keys
  const schoolId = typeof window !== 'undefined' ? localStorage.getItem('schoolId') : null;
  const phase = typeof window !== 'undefined' ? (localStorage.getItem('schoolAdmin_activePhase') || '') : '';
  const ayKey = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';
  const ayId = typeof window !== 'undefined' ? (localStorage.getItem(ayKey) || localStorage.getItem('currentAcademicYearId') || '') : '';
  const keySuffix = `${schoolId || 'ns'}|${phase || 'ns'}|${ayId || 'ns'}`;
  const keyExams = `examCenter:exams:${keySuffix}`;
  const keyClasses = `examCenter:classes:${keySuffix}`;
  const keySubjects = `examCenter:subjects:${keySuffix}`;
  const keyTeachers = `examCenter:teachers:${keySuffix}`;
  const keySessions = `examCenter:sessions:${keySuffix}`;
  const keyResults = `examCenter:results:${keySuffix}`;

  const safeParse = (k) => { try { return JSON.parse(localStorage.getItem(k) || '[]'); } catch { return []; } };

  const [exams, setExams] = useState(safeParse(keyExams));
  const [classes, setClasses] = useState(safeParse(keyClasses));
  const [subjects, setSubjects] = useState(safeParse(keySubjects));
  const [teachers, setTeachers] = useState(safeParse(keyTeachers));
  const [sessions, setSessions] = useState(safeParse(keySessions));
  const [results, setResults] = useState(safeParse(keyResults));
  const [loading, setLoading] = useState(true);
  const guardRef = useRef(null);
  const [showExamForm, setShowExamForm] = useState(false);
  const [editingExam, setEditingExam] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    classId: '',
    subjectId: '',
    teacherId: '',
    sessionId: '',
    date: '',
    startTime: '',
    endTime: '',
    maxMarks: '',
    passingMarks: '',
    type: 'regular',
    status: 'scheduled'
  });
  const [selectedClass, setSelectedClass] = useState('all');
  const [selectedSubject, setSelectedSubject] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [tabValue, setTabValue] = useState('all');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const examTypes = [
    { value: 'regular', label: t('academics.examControlCenter.examTypes.regular') },
    { value: 'midterm', label: t('academics.examControlCenter.examTypes.midterm') },
    { value: 'final', label: t('academics.examControlCenter.examTypes.final') },
    { value: 'quiz', label: t('academics.examControlCenter.examTypes.quiz') },
    { value: 'practical', label: t('academics.examControlCenter.examTypes.practical') }
  ];

  const examStatuses = [
    { value: 'scheduled', label: t('academics.examControlCenter.statuses.scheduled') },
    { value: 'ongoing', label: t('academics.examControlCenter.statuses.ongoing') },
    { value: 'completed', label: t('academics.examControlCenter.statuses.completed') },
    { value: 'cancelled', label: t('academics.examControlCenter.statuses.cancelled') }
  ];

  const getStatusLabel = (status) => {
    const match = examStatuses.find(s => s.value === status);
    return match ? match.label : status;
  };

  const getResultStatusLabel = (status) => {
    if (status === 'pass') return t('academics.examControlCenter.resultStatuses.pass');
    if (status === 'fail') return t('academics.examControlCenter.resultStatuses.fail');
    if (status === 'pending') return t('academics.examControlCenter.resultStatuses.pending');
    return status;
  };

  useEffect(() => {
    // 2s guard if there is no cache at all
    const hasAnyCache = (exams?.length || classes?.length || subjects?.length || teachers?.length || sessions?.length || results?.length);
    if (!hasAnyCache) {
      guardRef.current = setTimeout(() => setLoading(false), 2000);
    }
    fetchData();
    return () => { if (guardRef.current) clearTimeout(guardRef.current); };
  }, []);

  const fetchData = async () => {
    try {
      const [examsData, classesData, subjectsData, teachersData, sessionsData, resultsData] = await Promise.all([
        fetchJson('/evaluations', { method: 'GET' }),
        fetchJson('/classes', { method: 'GET' }),
        fetchJson('/subjects', { method: 'GET' }),
        fetchJson('/users?role=Teacher', { method: 'GET' }),
        fetchJson('/school-setup-and-configuration/academic-sessions', { method: 'GET' }),
        fetchJson('/academics/results', { method: 'GET' })
      ]);
      
      const ex = examsData.exams || examsData.data || examsData || [];
      const cls = classesData.classes || classesData.data || classesData || [];
      const sub = subjectsData.subjects || subjectsData.data || subjectsData || [];
      const tch = teachersData.users || teachersData.teachers || teachersData.data || teachersData || [];
      const ses = sessionsData.sessions || sessionsData.data || sessionsData || [];
      const rsl = resultsData.results || resultsData.data || resultsData || [];

      setExams(Array.isArray(ex) ? ex : []);
      setClasses(Array.isArray(cls) ? cls : []);
      setSubjects(Array.isArray(sub) ? sub : []);
      setTeachers(Array.isArray(tch) ? tch : []);
      setSessions(Array.isArray(ses) ? ses : []);
      setResults(Array.isArray(rsl) ? rsl : []);

      try {
        localStorage.setItem(keyExams, JSON.stringify(Array.isArray(ex) ? ex : []));
        localStorage.setItem(keyClasses, JSON.stringify(Array.isArray(cls) ? cls : []));
        localStorage.setItem(keySubjects, JSON.stringify(Array.isArray(sub) ? sub : []));
        localStorage.setItem(keyTeachers, JSON.stringify(Array.isArray(tch) ? tch : []));
        localStorage.setItem(keySessions, JSON.stringify(Array.isArray(ses) ? ses : []));
        localStorage.setItem(keyResults, JSON.stringify(Array.isArray(rsl) ? rsl : []));
      } catch (_) {}
    } catch (error) {
      showSnackbar(t('academics.examControlCenter.errors.fetchExamData'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const endpoint = editingExam 
        ? `/academics/exams/${editingExam.id}`
        : '/academics/exams';
      
      await fetchJson(endpoint, { method: editingExam ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(formData) });
      showSnackbar(editingExam ? t('academics.examControlCenter.messages.updated') : t('academics.examControlCenter.messages.created'), 'success');
      
      resetForm();
      fetchData();
    } catch (error) {
      showSnackbar(t('academics.examControlCenter.errors.saveExam'), 'error');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      classId: '',
      subjectId: '',
      teacherId: '',
      sessionId: '',
      date: '',
      startTime: '',
      endTime: '',
      maxMarks: '',
      passingMarks: '',
      type: 'regular',
      status: 'scheduled'
    });
    setEditingExam(null);
    setShowExamForm(false);
  };

  const handleEdit = (exam) => {
    setEditingExam(exam);
    setFormData(exam);
    setShowExamForm(true);
  };

  const handleDelete = async (id) => {
    try {
      await fetchJson(`/academics/exams/${id}`, { method: 'DELETE' });
      showSnackbar(t('academics.examControlCenter.messages.deleted'), 'success');
      fetchData();
    } catch (error) {
      showSnackbar(t('academics.examControlCenter.errors.deleteExam'), 'error');
    }
  };

  const handleStatusUpdate = async (id, status) => {
    try {
      await fetchJson(`/academics/exams/${id}/status`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status }) });
      showSnackbar(t('academics.examControlCenter.messages.statusUpdated', { status: getStatusLabel(status) }), 'success');
      fetchData();
    } catch (error) {
      showSnackbar(t('academics.examControlCenter.errors.updateStatus'), 'error');
    }
  };

  const getClassName = (classId) => {
    if (!classId || !Array.isArray(classes)) return t('academics.examControlCenter.labels.na');
    const cls = classes.find(c => c.id === classId);
    return cls ? cls.name : t('academics.examControlCenter.labels.na');
  };

  const getSubjectName = (subjectId) => {
    if (!subjectId || !Array.isArray(subjects)) return t('academics.examControlCenter.labels.na');
    const subject = subjects.find(s => s.id === subjectId);
    return subject ? subject.name : t('academics.examControlCenter.labels.na');
  };

  const getTeacherName = (teacherId) => {
    if (!teacherId || !Array.isArray(teachers)) return t('academics.examControlCenter.labels.na');
    const teacher = teachers.find(t => t.id === teacherId);
    return teacher ? `${teacher.firstName} ${teacher.lastName}` : t('academics.examControlCenter.labels.na');
  };

  const getExamStats = () => {
    if (!Array.isArray(exams)) return { total: 0, scheduled: 0, ongoing: 0, completed: 0 };
    const total = exams.length;
    const scheduled = exams.filter(e => e.status === 'scheduled').length;
    const ongoing = exams.filter(e => e.status === 'ongoing').length;
    const completed = exams.filter(e => e.status === 'completed').length;
    return { total, scheduled, ongoing, completed };
  };

  const filteredExams = Array.isArray(exams) ? exams.filter(exam => {
    return (selectedClass === 'all' || exam.classId === selectedClass) &&
           (selectedSubject === 'all' || exam.subjectId === selectedSubject) &&
           (selectedStatus === 'all' || exam.status === selectedStatus);
  }) : [];

  const stats = getExamStats();

  if (loading) {
    const hasAnyCache = (exams?.length || classes?.length || subjects?.length || teachers?.length || sessions?.length || results?.length);
    if (!hasAnyCache) {
      return (
        <AppLoader
          variant="circular3"
          size={56}
          centered
          messages={[t('academics.examControlCenter.loading.examData'), t('academics.examControlCenter.loading.applyingPhase'), t('academics.examControlCenter.loading.applyingAcademicYear')]}
        />
      );
    }
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
        <h1 className="text-2xl font-bold">{t('academics.examControlCenter.title')}</h1>
        <Button variant="contained" onClick={() => setShowExamForm(true)}>{t('academics.examControlCenter.actions.scheduleExam')}</Button>
        <Dialog open={showExamForm} onClose={() => setShowExamForm(false)} fullWidth maxWidth="md">
          <DialogTitle>{editingExam ? t('academics.examControlCenter.dialog.editTitle') : t('academics.examControlCenter.dialog.scheduleTitle')}</DialogTitle>
          <DialogContent>
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <TextField label={t('academics.examControlCenter.fields.examName')} value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />

                <FormControl required>
                  <InputLabel id="class-label">{t('academics.examControlCenter.fields.class')}</InputLabel>
                  <Select labelId="class-label" label={t('academics.examControlCenter.fields.class')} value={formData.classId} onChange={(e) => setFormData({ ...formData, classId: e.target.value })}>
                    {Array.isArray(classes) ? classes.map(cls => (
                      <MenuItem key={cls.id} value={cls.id}>{cls.name}</MenuItem>
                    )) : []}
                  </Select>
                </FormControl>

                <FormControl required>
                  <InputLabel id="subject-label">{t('academics.examControlCenter.fields.subject')}</InputLabel>
                  <Select labelId="subject-label" label={t('academics.examControlCenter.fields.subject')} value={formData.subjectId} onChange={(e) => setFormData({ ...formData, subjectId: e.target.value })}>
                    {Array.isArray(subjects) ? subjects.map(subject => (
                      <MenuItem key={subject.id} value={subject.id}>{subject.name}</MenuItem>
                    )) : []}
                  </Select>
                </FormControl>

                <FormControl required>
                  <InputLabel id="teacher-label">{t('academics.examControlCenter.fields.teacher')}</InputLabel>
                  <Select labelId="teacher-label" label={t('academics.examControlCenter.fields.teacher')} value={formData.teacherId} onChange={(e) => setFormData({ ...formData, teacherId: e.target.value })}>
                    {Array.isArray(teachers) ? teachers.map(teacher => (
                      <MenuItem key={teacher.id} value={teacher.id}>{teacher.firstName} {teacher.lastName}</MenuItem>
                    )) : []}
                  </Select>
                </FormControl>

                <FormControl>
                  <InputLabel id="type-label">{t('academics.examControlCenter.fields.examType')}</InputLabel>
                  <Select labelId="type-label" label={t('academics.examControlCenter.fields.examType')} value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })}>
                    {examTypes.map(type => (
                      <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl required>
                  <InputLabel id="session-label">{t('academics.examControlCenter.fields.session')}</InputLabel>
                  <Select labelId="session-label" label={t('academics.examControlCenter.fields.session')} value={formData.sessionId} onChange={(e) => setFormData({ ...formData, sessionId: e.target.value })}>
                    {sessions.map(session => (
                      <MenuItem key={session.id} value={session.id}>{session.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField type="date" label={t('academics.examControlCenter.fields.examDate')} InputLabelProps={{ shrink: true }} value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} required />
                <TextField type="time" label={t('academics.examControlCenter.fields.startTime')} InputLabelProps={{ shrink: true }} value={formData.startTime} onChange={(e) => setFormData({ ...formData, startTime: e.target.value })} required />
                <TextField type="time" label={t('academics.examControlCenter.fields.endTime')} InputLabelProps={{ shrink: true }} value={formData.endTime} onChange={(e) => setFormData({ ...formData, endTime: e.target.value })} required />
                <TextField type="number" label={t('academics.examControlCenter.fields.maxMarks')} value={formData.maxMarks} onChange={(e) => setFormData({ ...formData, maxMarks: e.target.value })} required />
                <TextField type="number" label={t('academics.examControlCenter.fields.passingMarks')} value={formData.passingMarks} onChange={(e) => setFormData({ ...formData, passingMarks: e.target.value })} required />
                <TextField label={t('academics.examControlCenter.fields.description')} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
              </Box>
              <DialogActions sx={{ mt: 2 }}>
                <Button type="button" onClick={resetForm} color="inherit">{t('common.cancel')}</Button>
                <Button type="submit" variant="contained">{editingExam ? t('academics.examControlCenter.actions.updateExam') : t('academics.examControlCenter.actions.scheduleExam')}</Button>
              </DialogActions>
            </Box>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardHeader title={t('academics.examControlCenter.stats.totalExams')} />
          <CardContent>
            <Box sx={{ fontSize: 24, fontWeight: 700 }}>{stats.total}</Box>
          </CardContent>
        </Card>
        <Card>
          <CardHeader title={t('academics.examControlCenter.statuses.scheduled')} />
          <CardContent>
            <Box sx={{ fontSize: 24, fontWeight: 700, color: 'primary.main' }}>{stats.scheduled}</Box>
          </CardContent>
        </Card>
        <Card>
          <CardHeader title={t('academics.examControlCenter.statuses.ongoing')} />
          <CardContent>
            <Box sx={{ fontSize: 24, fontWeight: 700, color: 'warning.main' }}>{stats.ongoing}</Box>
          </CardContent>
        </Card>
        <Card>
          <CardHeader title={t('academics.examControlCenter.statuses.completed')} />
          <CardContent>
            <Box sx={{ fontSize: 24, fontWeight: 700, color: 'success.main' }}>{stats.completed}</Box>
          </CardContent>
        </Card>
      </div>

      <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab value="all" label={t('academics.examControlCenter.tabs.all')} />
          <Tab value="scheduled" label={t('academics.examControlCenter.tabs.scheduled')} />
          <Tab value="ongoing" label={t('academics.examControlCenter.tabs.ongoing')} />
          <Tab value="completed" label={t('academics.examControlCenter.tabs.completed')} />
          <Tab value="results" label={t('academics.examControlCenter.tabs.results')} />
        </Box>

        {tabValue === 'all' && (
          <Card>
            <CardHeader title={t('academics.examControlCenter.sections.allExams')} />
            <CardContent>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <FormControl sx={{ minWidth: 180 }}>
                  <InputLabel id="filter-class">{t('academics.examControlCenter.filters.class.label')}</InputLabel>
                  <Select labelId="filter-class" label={t('academics.examControlCenter.filters.class.label')} value={selectedClass} onChange={(e) => setSelectedClass(e.target.value)}>
                    <MenuItem value="all">{t('academics.examControlCenter.filters.class.all')}</MenuItem>
                    {classes.map(cls => (
                      <MenuItem key={cls.id} value={cls.id}>{cls.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl sx={{ minWidth: 180 }}>
                  <InputLabel id="filter-subject">{t('academics.examControlCenter.filters.subject.label')}</InputLabel>
                  <Select labelId="filter-subject" label={t('academics.examControlCenter.filters.subject.label')} value={selectedSubject} onChange={(e) => setSelectedSubject(e.target.value)}>
                    <MenuItem value="all">{t('academics.examControlCenter.filters.subject.all')}</MenuItem>
                    {subjects.map(subject => (
                      <MenuItem key={subject.id} value={subject.id}>{subject.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl sx={{ minWidth: 180 }}>
                  <InputLabel id="filter-status">{t('academics.examControlCenter.filters.status.label')}</InputLabel>
                  <Select labelId="filter-status" label={t('academics.examControlCenter.filters.status.label')} value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
                    <MenuItem value="all">{t('academics.examControlCenter.filters.status.all')}</MenuItem>
                    {examStatuses.map(status => (
                      <MenuItem key={status.value} value={status.value}>{status.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('academics.examControlCenter.table.headers.name')}</TableCell>
                    <TableCell>{t('academics.examControlCenter.table.headers.class')}</TableCell>
                    <TableCell>{t('academics.examControlCenter.table.headers.subject')}</TableCell>
                    <TableCell>{t('academics.examControlCenter.table.headers.teacher')}</TableCell>
                    <TableCell>{t('academics.examControlCenter.table.headers.date')}</TableCell>
                    <TableCell>{t('academics.examControlCenter.table.headers.time')}</TableCell>
                    <TableCell>{t('academics.examControlCenter.table.headers.maxMarks')}</TableCell>
                    <TableCell>{t('academics.examControlCenter.table.headers.status')}</TableCell>
                    <TableCell>{t('academics.examControlCenter.table.headers.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredExams.map((exam) => (
                    <TableRow key={exam.id}>
                      <TableCell className="font-medium">{exam.name}</TableCell>
                      <TableCell>{getClassName(exam.classId)}</TableCell>
                      <TableCell>{getSubjectName(exam.subjectId)}</TableCell>
                      <TableCell>{getTeacherName(exam.teacherId)}</TableCell>
                      <TableCell>{new Date(exam.date).toLocaleDateString()}</TableCell>
                      <TableCell>{exam.startTime} - {exam.endTime}</TableCell>
                      <TableCell>{exam.maxMarks}</TableCell>
                      <TableCell>
                        <Chip label={getStatusLabel(exam.status)} color={
                          exam.status === 'scheduled' ? 'primary' :
                          exam.status === 'ongoing' ? 'warning' :
                          exam.status === 'completed' ? 'success' : 'error'
                        } size="small" />
                      </TableCell>
                      <TableCell>
                        <div className="flex space-x-2">
                          <Button size="small" variant="outlined" onClick={() => handleEdit(exam)}>{t('common.edit')}</Button>
                          <Button size="small" color="error" variant="outlined" onClick={() => handleDelete(exam.id)}>{t('common.delete')}</Button>
                          {exam.status === 'scheduled' && (
                            <Button size="small" onClick={() => handleStatusUpdate(exam.id, 'ongoing')}>{t('academics.examControlCenter.actions.start')}</Button>
                          )}
                          {exam.status === 'ongoing' && (
                            <Button size="small" onClick={() => handleStatusUpdate(exam.id, 'completed')}>{t('academics.examControlCenter.actions.complete')}</Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {tabValue === 'results' && (
          <Card>
            <CardHeader title={t('academics.examControlCenter.sections.examResults')} />
            <CardContent>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('academics.examControlCenter.results.headers.student')}</TableCell>
                    <TableCell>{t('academics.examControlCenter.results.headers.exam')}</TableCell>
                    <TableCell>{t('academics.examControlCenter.results.headers.marksObtained')}</TableCell>
                    <TableCell>{t('academics.examControlCenter.results.headers.maxMarks')}</TableCell>
                    <TableCell>{t('academics.examControlCenter.results.headers.percentage')}</TableCell>
                    <TableCell>{t('academics.examControlCenter.results.headers.grade')}</TableCell>
                    <TableCell>{t('academics.examControlCenter.results.headers.status')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.map((result) => (
                    <TableRow key={result.id}>
                      <TableCell>{result.studentName}</TableCell>
                      <TableCell>{result.examName}</TableCell>
                      <TableCell>{result.marksObtained}</TableCell>
                      <TableCell>{result.maxMarks}</TableCell>
                      <TableCell>
                        {((result.marksObtained / result.maxMarks) * 100).toFixed(1)}%
                      </TableCell>
                      <TableCell>
                        <Chip label={result.grade} size="small" />
                      </TableCell>
                      <TableCell>
                        <Chip label={getResultStatusLabel(result.status)} color={
                          result.status === 'pass' ? 'success' :
                          result.status === 'fail' ? 'error' : 'warning'
                        } size="small" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </Tabs>
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ExamControlCenterPage;
